import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { departmentsAPI, feedbackAPI, settingsAPI } from '../services/api';
import StarRating from '../components/feedback/StarRating';
import './FeedbackPage.css';

export default function FeedbackPage() {
  const [departments, setDepartments]   = useState([]);
  const [entries, setEntries]           = useState({});
  const [status, setStatus]             = useState(null);
  const [gateOpen, setGateOpen]         = useState(true);   // ← feedback gate
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        // Check gate FIRST — if closed, no need to load departments
        const [gateRes, deptRes, statusRes] = await Promise.all([
          settingsAPI.getFeedbackGate(),
          departmentsAPI.getAll(),
          feedbackAPI.getStatus(),
        ]);

        setGateOpen(gateRes.data.feedback_open);
        setDepartments(deptRes.data.departments);
        setStatus(statusRes.data);

        const initial = {};
        deptRes.data.departments.forEach((d) => {
          initial[d.id] = { rating: 0, comment: '' };
        });
        setEntries(initial);
      } catch {
        toast.error('Failed to load data. Is the backend running?');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const updateEntry = (deptId, field, value) => {
    setEntries((prev) => ({ ...prev, [deptId]: { ...prev[deptId], [field]: value } }));
  };

  const ratedEntries = departments.filter((d) => entries[d.id]?.rating > 0);
  const ratedCount   = ratedEntries.length;
  const totalCount   = departments.length;

  const handleSubmit = async () => {
    if (ratedCount === 0) {
      toast.error('Please rate at least one department before submitting');
      return;
    }

    const payload = departments
      .filter((d) => entries[d.id]?.rating > 0)
      .map((d) => ({
        department_id: d.id,
        rating:        entries[d.id].rating,
        comment:       entries[d.id].comment?.trim() || '',
      }));

    setSubmitting(true);
    try {
      await feedbackAPI.submit(payload);
      setSubmitted(true);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fb-page">
        <div className="flex-center" style={{ minHeight: '60vh' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  // ── FEEDBACK GATE CLOSED ───────────────────────────────────────────────────
  if (!gateOpen) {
    return (
      <div className="fb-page">
        <div className="fb-result animate-scale fb-result--closed">
          <div className="fb-result-icon">🔒</div>
          <h2>Feedback is closed for this semester</h2>
          <p>
            The feedback window is currently closed. Please check back when
            the next feedback session begins.
          </p>
          <p className="text-muted text-small mt-2">
            Contact your administrator for more information.
          </p>
        </div>
      </div>
    );
  }

  // ── Already submitted ──────────────────────────────────────────────────────
  if (status?.submitted) {
    return (
      <div className="fb-page">
        <div className="fb-result animate-scale">
          <div className="fb-result-icon">📋</div>
          <h2>Already Submitted</h2>
          <p>
            You have already submitted feedback for <strong>{status.year}</strong>.
          </p>
          <p className="text-muted text-small mt-2">
            Submitted on {new Date(status.submission?.created_at).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="fb-page">
        <div className="fb-result fb-result--success animate-scale" ref={topRef}>
          <div className="fb-result-icon">🎉</div>
          <h2>Thank you for your valuable feedback!</h2>
          <p>Your responses have been recorded successfully.</p>
          <div className="fb-submitted-summary">
            {ratedEntries.map((d) => (
              <div key={d.id} className="fb-submitted-row">
                <span className="fb-submitted-emoji">{getDeptEmoji(d.name)}</span>
                <span className="fb-submitted-name">{d.name}</span>
                <span className="fb-submitted-stars">
                  {[1,2,3,4,5].map((s) => (
                    <span key={s} style={{ color: s <= entries[d.id]?.rating ? '#FCB017' : '#ddd' }}>★</span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── No departments ─────────────────────────────────────────────────────────
  if (departments.length === 0) {
    return (
      <div className="fb-page">
        <div className="fb-result animate-scale">
          <div className="fb-result-icon">🏢</div>
          <h2>No Departments Available</h2>
          <p>There are no departments configured yet. Please check back later.</p>
        </div>
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="fb-page" ref={topRef}>
      <div className="fb-container animate-fade">

        {/* Header */}
        <div className="fb-header">
          <div>
            <h1>Annual Department Feedback — {new Date().getFullYear()}</h1>
            <p className="text-muted">
              Rate the departments you have interacted with. Unrated departments will be skipped.
            </p>
          </div>
          <div className="fb-rated-badge">
            <span className="fb-rated-num">{ratedCount}</span>
            <span className="fb-rated-label">of {totalCount} rated</span>
          </div>
        </div>

        {/* Sticky progress */}
        <div className="fb-sticky-bar">
          <div className="fb-progress-track">
            <div
              className="fb-progress-fill"
              style={{ width: `${totalCount > 0 ? (ratedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <span className="fb-sticky-text">
            {ratedCount === 0
              ? 'Rate at least one department to submit'
              : `${ratedCount} department${ratedCount > 1 ? 's' : ''} rated`}
          </span>
        </div>

        {/* Horizontal table */}
        <div className="fb-table-card card">
          <div className="fb-table-header">
            <div className="fb-col-dept">Department</div>
            <div className="fb-col-rating">Rating</div>
            <div className="fb-col-comment">Comments <span className="optional">(optional)</span></div>
          </div>
          <div className="fb-table-body">
            {departments.map((dept, index) => {
              const entry   = entries[dept.id] || { rating: 0, comment: '' };
              const isRated = entry.rating > 0;
              return (
                <div
                  key={dept.id}
                  className={`fb-table-row animate-fade ${isRated ? 'fb-row--rated' : ''}`}
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  <div className="fb-col-dept">
                    <div className="fb-dept-info">
                      <span className="fb-dept-emoji">{getDeptEmoji(dept.name)}</span>
                      <div>
                        <div className="fb-dept-name">{dept.name}</div>
                        {dept.description && (
                          <div className="fb-dept-desc">{dept.description}</div>
                        )}
                      </div>
                    </div>
                    {isRated && <span className="fb-rated-check">✓ Rated</span>}
                  </div>
                  <div className="fb-col-rating">
                    <StarRating
                      value={entry.rating}
                      onChange={(val) => updateEntry(dept.id, 'rating', val)}
                    />
                    {entry.rating > 0 && (
                      <button className="fb-clear-rating" onClick={() => updateEntry(dept.id, 'rating', 0)}>
                        ✕ clear
                      </button>
                    )}
                  </div>
                  <div className="fb-col-comment">
                    <textarea
                      className="form-textarea fb-comment-input"
                      rows={2}
                      placeholder={`Your experience with ${dept.name}…`}
                      value={entry.comment}
                      onChange={(e) => updateEntry(dept.id, 'comment', e.target.value)}
                      maxLength={500}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div className="fb-submit-section">
          {ratedCount === 0 && (
            <div className="fb-info-notice">
              ℹ Rate at least one department to enable submission.
              You can skip departments you haven't interacted with.
            </div>
          )}
          {ratedCount > 0 && ratedCount < totalCount && (
            <div className="fb-skip-notice">
              ℹ {totalCount - ratedCount} unrated department{totalCount - ratedCount > 1 ? 's' : ''} will
              be skipped. Only your {ratedCount} rated response{ratedCount > 1 ? 's' : ''} will be saved.
            </div>
          )}
          <button
            className={`btn btn-lg btn-full ${ratedCount > 0 ? 'btn-accent' : 'btn-primary'}`}
            onClick={handleSubmit}
            disabled={submitting || ratedCount === 0}
          >
            {submitting
              ? <><span className="spinner spinner-sm" /> Submitting…</>
              : `Submit Feedback (${ratedCount} department${ratedCount !== 1 ? 's' : ''})`}
          </button>
          <p className="fb-submit-note">
            Feedback is collected once per year. You cannot edit after submission.
          </p>
        </div>

      </div>
    </div>
  );
}

function getDeptEmoji(name) {
  const n = name.toLowerCase();
  if (n.includes('engineer') || n.includes('tech'))   return '⚙️';
  if (n.includes('hr') || n.includes('human'))         return '👥';
  if (n.includes('finance') || n.includes('account'))  return '💰';
  if (n.includes('market'))                            return '📣';
  if (n.includes('sales'))                             return '📈';
  if (n.includes('support') || n.includes('customer')) return '🎧';
  if (n.includes('operat') || n.includes('admin'))     return '🏢';
  if (n.includes('product'))                           return '🚀';
  return '🏬';
}
